import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { log } from 'lib/utils';

const TIMER_UPDATE_INTERVAL_MS = 1000;

const FlashOffersContext = createContext({ offers: new Map(), tick: 0 });

const isValidConfig = (data) => {
  if (!data || !Array.isArray(data.offers)) {
    return false;
  }

  return data.offers.every(
    (entry) => entry.offerId && entry.startTime && entry.endTime
  );
};

const buildOffersMap = (offers) => {
  const map = new Map();

  offers.forEach((entry) => {
    map.set(Number(entry.offerId), {
      offerId: Number(entry.offerId),
      startTime: entry.startTime,
      endTime: entry.endTime,
      theme: entry.theme || 'secondary',
    });
  });

  return map;
};

const FlashOffersProvider = ({ children }) => {
  const [flashOffers, setFlashOffers] = useState(() => new Map());
  const [tick, setTick] = useState(() => Date.now());

  useEffect(() => {
    const flashOffersUrl = process.env.NEXT_PUBLIC_FLASH_OFFERS_CONFIG_URL;

    if (!flashOffersUrl) {
      return;
    }

    const fetchFlashOffers = async () => {
      let error = null;
      let data = null;

      try {
        const response = await fetch(flashOffersUrl);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        data = await response.json();
      } catch (err) {
        error = err;
      }

      log('flash-offers', {
        enabled: data?.enabled ?? null,
        offers: data?.offers?.length ?? 0,
        error: error?.message ?? null,
      });

      if (error || !isValidConfig(data) || data.enabled === false) {
        return;
      }

      setFlashOffers(buildOffersMap(data.offers));
    };

    fetchFlashOffers();
  }, []);

  useEffect(() => {
    if (flashOffers.size === 0) {
      return;
    }

    const interval = setInterval(() => {
      setTick(Date.now());
    }, TIMER_UPDATE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [flashOffers.size]);

  const value = useMemo(
    () => ({ offers: flashOffers, tick }),
    [flashOffers, tick]
  );

  return (
    <FlashOffersContext.Provider value={value}>
      {children}
    </FlashOffersContext.Provider>
  );
};

export const useFlashOffers = () => useContext(FlashOffersContext);

FlashOffersProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default FlashOffersProvider;
